<template>
    <div class="card-wrap"
         @mousemove="handleMouseMove"
         @mouseenter="handleMouseEnter"
         @mouseleave="handleMouseLeave"
         ref="IndexListCard">
        <div class="card"
             :style="cardStyle">
            <div class="card-bg" :style="[cardBgTransform, cardBgImage]"></div>
            <div class="card-info">
                <h4 class="title">{{ title }}</h4>
                <p class="subTitle">{{ subtitle }}</p>
            </div>
        </div>
    </div>
</template>

<script setup>
import {defineProps, ref, computed, onMounted} from 'vue'

// 获取卡片DOM元素的引用
const IndexListCard = ref(null)

// 响应式数据
const width = ref(0)        // 卡片宽度
const height = ref(0)       // 卡片高度
const mouseX = ref(0)       // 鼠标X坐标（相对于卡片中心）
const mouseY = ref(0)       // 鼠标Y坐标（相对于卡片中心）
const mouseLeaveDelay = ref(null) // 鼠标离开延迟计时器
const props = defineProps({
  dataImage: {
    type: String,
    required: true
  },
    title: {
      type: String,
      required: true
    },
    subtitle: {
      type: String,
      required: true
    }
})

// 计算属性
// 计算鼠标X坐标相对于卡片宽度的比例（-0.5到0.5之间）
const mousePX = computed(() => mouseX.value / width.value)
// 计算鼠标Y坐标相对于卡片高度的比例（-0.5到0.5之间）
const mousePY = computed(() => mouseY.value / height.value)

// 卡片旋转样式计算
const cardStyle = computed(() => {
    // 根据鼠标位置计算X/Y轴旋转角度（最大30度）
    const rX = mousePX.value * 30  // X轴旋转（垂直方向）
    const rY = mousePY.value * -30 // Y轴旋转（水平方向）
    return {
        transform: `rotateY(${rX}deg) rotateX(${rY}deg)`
    }
})

// 背景图位移计算
const cardBgTransform = computed(() => {
    // 根据鼠标位置计算背景图位移（最大-40px）
    const tX = mousePX.value * -40 // X轴反向位移
    const tY = mousePY.value * -32 // Y轴反向位移
    return {
        transform: `translateX(${tX}px) translateY(${tY}px)`
    }
})

// 背景图片样式
const cardBgImage = computed(() => ({
    backgroundImage: `url(${props.dataImage})` // 使用props传入的图片地址
}))

// 方法
/**
 * 处理鼠标移动事件
 * @param {MouseEvent} e - 鼠标事件对象
 */
const handleMouseMove = (e) => {
    if (!IndexListCard.value) return
    // 获取卡片相对于视口的位置和尺寸
    const rect = IndexListCard.value.getBoundingClientRect()
    // 计算鼠标相对于卡片中心的坐标
    // e.pageX/Y获取文档坐标，减去卡片左上角坐标和卡片一半尺寸，得到以中心为原点的坐标
    mouseX.value = e.pageX - rect.left - width.value / 2
    mouseY.value = e.pageY - rect.top - height.value / 2
}

/**
 * 处理鼠标进入事件
 * 立即清除复位计时器，实现快速响应
 */
const handleMouseEnter = () => {
    clearTimeout(mouseLeaveDelay.value)
}

/**
 * 处理鼠标离开事件
 * 延迟1秒后复位鼠标坐标，实现平滑的复位动画
 */
const handleMouseLeave = () => {
    mouseLeaveDelay.value = setTimeout(() => {
        mouseX.value = 0  // 重置X坐标
        mouseY.value = 0  // 重置Y坐标
    }, 1000)
}

// 生命周期
onMounted(() => {
    if (IndexListCard.value) {
        // 获取卡片实际渲染尺寸
        width.value = IndexListCard.value.offsetWidth
        height.value = IndexListCard.value.offsetHeight
    }
})
</script>

<style scoped lang="scss">

$hoverEasing: cubic-bezier(0.23, 1, 0.32, 1);
$returnEasing: cubic-bezier(0.445, 0.05, 0.55, 0.95);

.card-wrap {
  margin: 10px;
  transform: perspective(800px);
  transform-style: preserve-3d;
  cursor: pointer;
  // background-color: #fff;

  &:hover {
    .card-info {
      transform: translateY(0);
    }

    .card-info p {
      opacity: 1;
        height: 80px;
        font-size: 12px;
        display: -webkit-box; /* 将对象作为弹性伸缩盒子模型显示 */
        -webkit-box-orient: vertical; /* 设置子元素的排列方式为垂直 */
        -webkit-line-clamp: 5; /* 限制在块元素中显示的文本行数 */
        overflow: hidden; /* 隐藏超出容器的内容 */
    }

    .card-info, .card-info p {
      transition: 0.6s $hoverEasing;
    }

    .card-info:after {
      //transition: 5s $hoverEasing;
      //opacity: 1;
      //transform: translateY(0);
    }

    .card-bg {
      transition: 0.6s $hoverEasing, opacity 1.5s $hoverEasing;
      opacity: 0.8;
    }

    .card {
      transition: 0.6s $hoverEasing,
      box-shadow 2s $hoverEasing;
      box-shadow: rgba(white, 0.2) 0 0 40px 5px,
      rgba(white, 1) 0 0 0 1px,
      rgba(black, 0.66) 0 10px 20px 0,
      inset #333 0 0 0 5px,
      inset white 0 0 0 6px;
    }
  }
}

.card {
  position: relative;
  flex: 0 0 240px;
  width: 140px;
  height: 220px;
  background-color: #333;
  overflow: hidden;
  border-radius: 10px;
  box-shadow: rgba(black, 0.66) 0 10px 20px 0,
  inset #333 0 0 0 5px,
  inset rgba(white, 0.5) 0 0 0 6px;
  transition: 1s $returnEasing;
}

.card-bg {
  opacity: 0.5;
  position: absolute;
  top: -20px;
  left: -20px;
  width: 100%;
  height: 100%;
  padding: 20px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  transition: 1s $returnEasing,
  opacity 5s 1s $returnEasing;
  pointer-events: none;
}

.card-info {
  padding: 20px;
  //position: absolute;
  bottom: 0;
  color: #fff;
  transform: translateY(40%);
  transition: 0.6s 1.6s cubic-bezier(0.215, 0.61, 0.355, 1);

  p {
    //opacity: 0.7;
    text-shadow: rgba(black, 1) 0 2px 3px;
    transition: 0.6s 1.6s cubic-bezier(0.215, 0.61, 0.355, 1);

      height: 50px;
    font-size: 12px;

      display: -webkit-box; /* 将对象作为弹性伸缩盒子模型显示 */
-webkit-box-orient: vertical; /* 设置子元素的排列方式为垂直 */
-webkit-line-clamp: 5; /* 限制在块元素中显示的文本行数 */
overflow: hidden; /* 隐藏超出容器的内容 */
  }

  * {
    position: relative;
    z-index: 1;
  }

  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    z-index: 0;
    width: 100%;
    height: 100%;
    background-image: linear-gradient(to bottom, transparent 0%, rgba(#000, 0.6) 100%);
    background-blend-mode: overlay;
    opacity: 0;
    transform: translateY(100%);
    transition: 5s 1s $returnEasing;
  }
}

.card-info h1 {
  font-size: 36px;
  font-weight: 700;
  text-shadow: rgba(black, 0.5) 0 10px 10px;
}
</style>